import { Button } from '~/components/ui/button'

/**
 * `loading` drives the spinner AND disables the button; `disabled` only
 * disables it. Split in #512 so the sign-up consent gate can hold these
 * buttons closed until the user ticks the box without pretending something is
 * in flight — a spinner on an untouched form reads as "working", not "blocked".
 *
 * `disabled` is expressed as aria-disabled, NOT the native attribute (#869).
 * A native `disabled` button drops the click before React sees it, so the
 * caller's "please accept the Terms" message could never fire from a mouse —
 * the page just looked broken. The click still reaches onClick; the caller is
 * responsible for turning it away and saying why. Only `loading` disables for
 * real, because there a second click would double-submit.
 */
const blockedClasses = 'opacity-60 cursor-not-allowed'
type SocialButtonProps<K extends string> = Record<K, () => void> & {
  loading: boolean
  disabled?: boolean
  /**
   * #871 — "Continue with Google" is the same word on both pages, so neither
   * says which one you are on. On a sign-up route the button MAKES an account;
   * name that, since it is the difference the user cares about.
   */
  isSignup?: boolean
}

const verb = (isSignup?: boolean) => (isSignup ? 'Sign up' : 'Continue')

export const GoogleButton = ({ handleGoogleLogin, loading, disabled, isSignup }: SocialButtonProps<'handleGoogleLogin'>) => {
    return (
        <Button
          onClick={handleGoogleLogin}
          disabled={loading}
          aria-disabled={disabled || undefined}
          className={`w-full h-11 bg-white text-gray-700 border-2 border-gray-300 font-medium shadow-sm ${disabled ? blockedClasses : 'hover:bg-gray-50'}`}
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {verb(isSignup)} with Google
            </>
          )}
        </Button>
    )
}

export const FacebookButton = ({ handleFacebookLogin, loading, disabled, isSignup }: SocialButtonProps<'handleFacebookLogin'>) => {
    return (
        <Button
          onClick={handleFacebookLogin}
          disabled={loading}
          aria-disabled={disabled || undefined}
          className={`w-full h-11 bg-[#1877F2] text-white font-medium shadow-sm ${disabled ? blockedClasses : 'hover:bg-[#166FE5]'}`}
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="white">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              {verb(isSignup)} with Facebook
            </>
          )}
        </Button>
    )
}

export const AppleButton = ({ handleAppleLogin, loading, disabled, isSignup }: SocialButtonProps<'handleAppleLogin'>) => {
    return (
        <Button
          onClick={handleAppleLogin}
          disabled={loading}
          aria-disabled={disabled || undefined}
          className={`w-full h-11 bg-black text-white font-medium shadow-sm ${disabled ? blockedClasses : 'hover:bg-gray-900'}`}
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="white">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              {verb(isSignup)} with Apple
            </>
          )}
        </Button>
    )
}